import { useState, useEffect } from 'react';
import api from '../services/api';
import Navigation from '../components/Navigation';
import {
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiEye,
  FiRefreshCw,
} from 'react-icons/fi';

const AdminDashboard = () => {
  const [contractors, setContractors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sortBy: 'submitted_at',
    sortOrder: 'DESC',
    limit: 50,
    offset: 0,
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contractorsData, statsData] = await Promise.all([
        api.getContractors(filters),
        api.getAdminStats(),
      ]);
      setContractors(contractorsData.contractors || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (contractorId, newStatus) => {
    try {
      await api.updateContractorStatus(contractorId, newStatus);
      await loadData();
      if (selectedContractor?.id === contractorId) {
        setSelectedContractor({ ...selectedContractor, status: newStatus });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const viewContractor = async (id) => {
    try {
      const data = await api.getContractorById(id);
      setSelectedContractor(data.contractor);
    } catch (error) {
      console.error('Failed to load contractor:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: (
        <span className="px-3 py-1 bg-slate-500/20 text-slate-300 rounded-full text-sm font-medium">
          Draft
        </span>
      ),
      pending: (
        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-medium flex items-center gap-1">
          <FiClock className="w-4 h-4" />
          Pending
        </span>
      ),
      approved: (
        <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-medium flex items-center gap-1">
          <FiCheckCircle className="w-4 h-4" />
          Approved
        </span>
      ),
      rejected: (
        <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm font-medium flex items-center gap-1">
          <FiXCircle className="w-4 h-4" />
          Rejected
        </span>
      ),
    };
    return badges[status] || badges.draft;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navigation />
      <div className="container mx-auto px-4 py-12 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
              <p className="text-slate-300">Manage contractor onboarding applications</p>
            </div>
            <button
              onClick={loadData}
              className="mt-4 md:mt-0 px-4 py-2 glass-button text-white font-semibold rounded-lg hover:bg-white/30 flex items-center gap-2"
            >
              <FiRefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="glass-card p-6">
                <div className="text-3xl font-bold text-white mb-1">
                  {stats.contractors?.total || 0}
                </div>
                <div className="text-slate-300 text-sm">Total Contractors</div>
              </div>
              <div className="glass-card p-6">
                <div className="text-3xl font-bold text-yellow-400 mb-1">
                  {stats.contractors?.byStatus?.pending || 0}
                </div>
                <div className="text-slate-300 text-sm">Pending Review</div>
              </div>
              <div className="glass-card p-6">
                <div className="text-3xl font-bold text-green-400 mb-1">
                  {stats.contractors?.byStatus?.approved || 0}
                </div>
                <div className="text-slate-300 text-sm">Approved</div>
              </div>
              <div className="glass-card p-6">
                <div className="text-3xl font-bold text-blue-400 mb-1">
                  {stats.contractors?.recentSubmissions || 0}
                </div>
                <div className="text-slate-300 text-sm">Recent Submissions</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="glass-card p-6 mb-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value, offset: 0 })}
                    placeholder="Search by name, email, ID..."
                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value, offset: 0 })
                  }
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Sort By</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="submitted_at">Submitted Date</option>
                  <option value="created_at">Created Date</option>
                  <option value="full_name">Name</option>
                  <option value="status">Status</option>
                  <option value="application_id">Application ID</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Order</label>
                <button
                  onClick={() =>
                    setFilters({
                      ...filters,
                      sortOrder: filters.sortOrder === 'ASC' ? 'DESC' : 'ASC',
                    })
                  }
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 flex items-center justify-between"
                >
                  <span>{filters.sortOrder === 'ASC' ? 'Ascending' : 'Descending'}</span>
                  {filters.sortOrder === 'ASC' ? (
                    <FiChevronUp className="w-5 h-5" />
                  ) : (
                    <FiChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Contractors Table */}
          <div className="glass-card">
            {loading ? (
              <div className="p-12 text-center text-white">Loading...</div>
            ) : contractors.length === 0 ? (
              <div className="p-12 text-center text-slate-300">No contractors found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Application ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Trades
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Submitted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {contractors.map((contractor) => (
                      <tr key={contractor.id} className="hover:bg-white/5">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-white">
                            {contractor.application_id || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            {contractor.full_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300">
                            {contractor.user_email || contractor.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-300">
                            {contractor.trades?.map((t) => t.name).join(', ') || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(contractor.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300">
                            {contractor.submitted_at
                              ? new Date(contractor.submitted_at).toLocaleDateString()
                              : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => viewContractor(contractor.id)}
                            className="px-3 py-1 glass-button text-white text-sm font-semibold rounded-lg hover:bg-white/30 flex items-center gap-1"
                          >
                            <FiEye className="w-4 h-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Extra spacing at bottom to ensure content is visible */}
          <div className="h-8"></div>

          {/* Contractor Detail Modal */}
          {selectedContractor && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="glass-card p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {selectedContractor.full_name || 'Contractor Details'}
                    </h2>
                    <p className="text-slate-300">
                      {selectedContractor.application_id || 'No Application ID'}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedContractor(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    <FiXCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Personal Info</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-slate-400">Email:</span>
                        <span className="text-white ml-2">
                          {selectedContractor.user_email || selectedContractor.email || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400">Telephone:</span>
                        <span className="text-white ml-2">
                          {selectedContractor.telephone || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400">Address:</span>
                        <span className="text-white ml-2">
                          {selectedContractor.address || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Business Info</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-slate-400">Business Name:</span>
                        <span className="text-white ml-2">
                          {selectedContractor.business_name || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400">Business Type:</span>
                        <span className="text-white ml-2">
                          {selectedContractor.business_type || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400">Trades:</span>
                        <span className="text-white ml-2">
                          {selectedContractor.trades?.map((t) => t.name).join(', ') || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Update Status</h3>
                  <div className="flex gap-3">
                    {['pending', 'approved', 'rejected'].map((status) => (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(selectedContractor.id, status)}
                        disabled={selectedContractor.status === status}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          status === 'approved'
                            ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                            : status === 'rejected'
                            ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                            : 'bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="text-sm text-slate-400">
                  <div>
                    Created: {new Date(selectedContractor.created_at).toLocaleString()}
                  </div>
                  {selectedContractor.submitted_at && (
                    <div>
                      Submitted: {new Date(selectedContractor.submitted_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
